import { z } from 'zod';
import type { SigninRedirectArgs } from 'oidc-client-ts';
import { Environment, getEnvironmentBySubDomain } from './configuration/login/environmentUtils';

const asBool = (value: string | undefined): boolean => value === 'true';

type LoginEnvironmentConfig = {
    redirectUri: string;
    silentRedirectUri: string;
    appBaseUrl: string;
    signinOptions: SigninRedirectArgs;
};

export type ConfigState = {
    login: {
        authority: string;
        clientId: string;
        oauthScope: string[];
        mockAuthorization: boolean;
        useFederatedLogin: boolean;
        mockLocale: string | undefined;
        mockTenant: string | undefined;
        mockLogisticsTenant: string | undefined;
        mockRole: string | undefined;
        mockDuns: string | undefined;
        preventRedirect: boolean;
        redirectUri: string;
        silentRedirectUri: string;
        appBaseUrl: string;
        signinOptions: SigninRedirectArgs;
    };
    sentry: {
        dsn: string | undefined;
        moduleName: string;
    };
    msw: {
        enabled: boolean;
    };
    transportExecutionApi: {
        baseUrl: string;
    };
    environmentBaseUrls: Record<Environment, string>;
};

const RIO_LOGIN_CONFIG: LoginEnvironmentConfig = {
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI,
    appBaseUrl: import.meta.env.VITE_APP_BASE_URL_RIO,
    signinOptions: {},
};

const VW_RIO_DEV_LOGIN_CONFIG: LoginEnvironmentConfig = {
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW_RIO_DEV,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW_RIO_DEV,
    appBaseUrl: import.meta.env.VITE_APP_BASE_URL_VW_RIO_DEV,
    signinOptions: {},
};

const VW_QA_LOGIN_CONFIG: LoginEnvironmentConfig = {
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW_QA,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW_QA,
    appBaseUrl: import.meta.env.VITE_APP_BASE_URL_VW_QA,
    signinOptions: { extraQueryParams: { idp: 'cloud-vwgroup-com-qa' } },
};

const VW_PROD_LOGIN_CONFIG: LoginEnvironmentConfig = {
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW_PROD,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW_PROD,
    appBaseUrl: import.meta.env.VITE_APP_BASE_URL_VW_PROD,
    signinOptions: { extraQueryParams: { idp: 'cloud-vwgroup-com' } },
};

const resolveEnvironmentLoginConfig = (environment: Environment): LoginEnvironmentConfig => {
    switch (environment) {
        case Environment.VW_RIO_DEV:
            return VW_RIO_DEV_LOGIN_CONFIG;
        case Environment.VW_QA:
            return VW_QA_LOGIN_CONFIG;
        case Environment.VW_PROD:
            return VW_PROD_LOGIN_CONFIG;
        case Environment.RIO:
            return RIO_LOGIN_CONFIG;
    }
};

export const config: ConfigState = {
    login: {
        authority: import.meta.env.VITE_LOGIN_AUTHORITY,
        clientId: import.meta.env.VITE_LOGIN_CLIENT_ID,
        oauthScope: ['openid', 'profile', 'email'],
        mockAuthorization: import.meta.env.DEV,
        useFederatedLogin: asBool(import.meta.env.VITE_LOGIN_USE_FEDERATED_LOGIN),
        mockLocale: import.meta.env.VITE_LOGIN_MOCK_LOCALE,
        mockTenant: import.meta.env.VITE_LOGIN_MOCK_TENANT,
        mockLogisticsTenant: import.meta.env.VITE_LOGIN_MOCK_LOGISTICS_TENANT,
        mockRole: import.meta.env.VITE_LOGIN_MOCK_USER_ROLE,
        mockDuns: import.meta.env.VITE_LOGIN_MOCK_USER_DUNS,
        preventRedirect: asBool(import.meta.env.VITE_LOGIN_PREVENT_REDIRECT),
        ...resolveEnvironmentLoginConfig(getEnvironmentBySubDomain()),
    },
    environmentBaseUrls: {
        [Environment.RIO]: RIO_LOGIN_CONFIG.appBaseUrl,
        [Environment.VW_RIO_DEV]: VW_RIO_DEV_LOGIN_CONFIG.appBaseUrl,
        [Environment.VW_QA]: VW_QA_LOGIN_CONFIG.appBaseUrl,
        [Environment.VW_PROD]: VW_PROD_LOGIN_CONFIG.appBaseUrl,
    },
    sentry: {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        moduleName: 'frontend-transport-execution',
    },
    msw: {
        enabled: import.meta.env.DEV,
    },
    transportExecutionApi: {
        baseUrl: import.meta.env.VITE_TRANSPORT_EXECUTION_API_BASE_URL,
    },
};

z.config({ jitless: true }); // See https://github.com/colinhacks/zod/issues/4461
